// Popup "Criar usuário" do Perfil (PWA, pedido 2026-09-10/11) — o titular
// gerencia sozinho quem ele já criou (editar/trocar senha/excluir) e cria
// gente nova a partir daqui. Um sub-usuário NUNCA vê este popup (o botão
// que abre isto já não aparece pra ele, ver mobile-app.tsx#YouPanel — e o
// backend recusa com 403 mesmo que tentasse chamar POST /sub-users direto).
import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Pencil, Plus, Trash2, UserPlus } from "lucide-react";
import type { Vehicle } from "@/types";
import { useDeleteSubUser, useMySubUsers } from "@/hooks/queries";
import { GpsDriveApiError } from "@/lib/api/gpsdrive-client";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { SubUserFormDialog } from "@/components/sub-user-form-dialog";

export function SubUsersManagerDialog({
  vehicles,
  open,
  onOpenChange,
}: {
  vehicles: Vehicle[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { data: subUsers = [], isLoading } = useMySubUsers(open);
  const deleteMutation = useDeleteSubUser();
  const [formMode, setFormMode] = useState<"create" | "edit" | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Excluir o usuário "${name}"? Essa pessoa perde o acesso imediatamente.`)) return;
    setDeletingId(id);
    try {
      await deleteMutation.mutateAsync(id);
      toast.success(`Usuário "${name}" excluído.`);
    } catch (err) {
      toast.error("Não foi possível excluir agora.", {
        description: err instanceof GpsDriveApiError ? err.message : undefined,
      });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-sm max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-sky-600" />
              Usuários criados por mim
            </DialogTitle>
            <DialogDescription>
              Pessoas com acesso aos seus veículos. Você pode editar, trocar a senha ou excluir a
              qualquer momento.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <Button
              className="w-full"
              onClick={() => {
                setFormMode("create");
                setEditingId(null);
              }}
            >
              <Plus className="mr-2 h-4 w-4" /> Criar novo usuário
            </Button>

            {isLoading ? (
              <div className="p-6 text-center text-sm text-muted-foreground">
                <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />
                Carregando…
              </div>
            ) : subUsers.length === 0 ? (
              <p className="rounded-md border border-dashed border-border p-6 text-center text-xs text-muted-foreground">
                Você ainda não criou nenhum usuário.
              </p>
            ) : (
              <ul className="divide-y divide-border rounded-md border border-border">
                {subUsers.map((u) => (
                  <li key={u.id} className="flex items-center justify-between gap-2 px-3 py-2.5">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium">{u.name}</div>
                      <div className="truncate text-xs text-muted-foreground">
                        {u.email}
                        {u.phone ? ` · ${u.phone}` : ""}
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <button
                        type="button"
                        title="Editar"
                        onClick={() => {
                          setFormMode("edit");
                          setEditingId(u.id);
                        }}
                        className="grid h-8 w-8 place-items-center rounded-md text-slate-400 hover:bg-sky-50 hover:text-sky-600"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        title="Excluir"
                        disabled={deletingId === u.id}
                        onClick={() => handleDelete(u.id, u.name)}
                        className="grid h-8 w-8 place-items-center rounded-md text-slate-400 hover:bg-rose-50 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {deletingId === u.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {formMode && (
        <SubUserFormDialog
          mode={formMode}
          vehicles={vehicles}
          subUserId={editingId ?? undefined}
          open={formMode !== null}
          onOpenChange={(next) => {
            if (!next) setFormMode(null);
          }}
        />
      )}
    </>
  );
}
